import { workspace } from '../../shared/api';
import { PspCpfLookupResponse } from '../../interfaces/medicine';
import { logger } from '../../helpers/logger';

/**
 * Resposta da consulta de CPF do PBM.
 *
 * Por enquanto compartilha o mesmo contrato do PSP (`PspCpfLookupResponse`).
 * Mantido como alias próprio do PBM para permitir divergência futura sem
 * impactar os consumidores.
 */
export type PbmCpfLookupResponse = PspCpfLookupResponse;

/**
 * Payload da consulta de CPF do PBM.
 */
export interface PbmCpfLookupPayload {
  /** Documento (CPF) do paciente. */
  document: string;
  /** Códigos EAN dos medicamentos elegíveis. */
  eans: string[];
  /** ID da prescrição. */
  prescriptionId: number;
  /** ID do paciente. */
  patientId: number;
  /** ID do médico prescritor. */
  doctorId: number;
}

/**
 * Payload do pré-cadastro do PBM.
 */
export interface PbmPreEnrollmentPayload {
  /** Código EAN do medicamento. */
  eanCode: string;
  /** ID do programa PBM. */
  programId: number;
  /** ID do parceiro. */
  partnerId: number;
  /** ID do paciente. */
  patientId: number;
  /** ID do médico prescritor. */
  doctorId: number;
  /** ID da prescrição. */
  prescriptionId: number;
  /** Telefone do paciente. */
  phone: string;
  /** E-mail do paciente. */
  email: string;
  /** Consentimento do paciente. */
  patientConsent: boolean;
  /** Consentimento do médico. */
  doctorConsent: boolean;
  /** Nome do paciente. */
  patientName: string;
  /** Documento (CPF) do paciente. */
  patientDocument: string;
  /** Data de nascimento do paciente. */
  patientBirthdate: string;
  /** Nome do médico. */
  doctorName: string;
  /** Documento do médico. */
  doctorDocument: string;
  /** UF do conselho do médico. */
  doctorLicenseState: string;
  /** Número do conselho do médico. */
  doctorLicenseNumber: string;
  /** Tipo de conselho do médico (ex.: CRM). */
  doctorLicenseType: string;
  /** E-mail do médico. */
  doctorEmail: string;
}

/**
 * Helper de API do PBM (Programa de Benefício em Medicamentos).
 *
 * Desacoplado do `MedicineService`. Por enquanto as chamadas apontam para os
 * mesmos endpoints do PSP e possuem a mesma assinatura; a separação permite
 * evoluir o PBM de forma independente quando os contratos mudarem.
 */
export const PbmService = {
  /**
   * Consulta se existe programa PBM para os medicamentos informados e se o
   * paciente já está cadastrado, a partir do CPF e dos códigos EAN.
   *
   * @param payload Dados da consulta (documento, EANs, IDs de prescrição, paciente e médico).
   * @returns Os dados do programa e status de cadastro, ou `null` quando faltarem parâmetros obrigatórios.
   * @throws Repassa o erro da requisição quando a chamada HTTP falha.
   */
  async pbmCpfLookup(payload: PbmCpfLookupPayload): Promise<PbmCpfLookupResponse | null> {
    try {
      if (
        !payload.document ||
        !payload.eans ||
        payload.eans.length === 0 ||
        !payload.patientId ||
        !payload.doctorId ||
        !payload.prescriptionId
      ) {
        return null;
      }

      const { data } = await workspace.post<PbmCpfLookupResponse>(
        `/psp-programs/cpf-lookup`,
        {
          eanCodes: payload.eans,
          patientDocument: payload.document.trim(),
          patientId: payload.patientId,
          doctorId: payload.doctorId,
          prescriptionId: payload.prescriptionId,
        },
        {
          timeout: 10000,
        }
      );

      return data;
    } catch (error) {
      logger.error('[PBM - CPF Lookup] Erro ao buscar dados do programa PBM:', { error });
      throw error;
    }
  },

  /**
   * Cria o pré-cadastro do paciente no programa PBM.
   *
   * @param payload Dados completos do pré-cadastro (medicamento, programa, paciente, médico e consentimentos).
   * @returns Os dados retornados pelo backend.
   * @throws Repassa o erro da requisição quando a chamada HTTP falha.
   */
  async pbmPreEnrollment(payload: PbmPreEnrollmentPayload) {
    const sanitizedPayload = {
      ...payload,
      phone: payload.phone.trim(),
      email: payload.email.trim(),
    };

    try {
      const { data } = await workspace.post(
        `/psp-programs/pre-enrollment`,
        {
          eanCode: payload.eanCode,
          programId: payload.programId,
          partnerId: payload.partnerId,
          patientName: payload.patientName,
          patientDocument: payload.patientDocument,
          birthdate: payload.patientBirthdate,
          patientId: payload.patientId,
          doctorId: payload.doctorId,
          phone: sanitizedPayload.phone,
          email: sanitizedPayload.email,
          patientConsent: payload.patientConsent,
          doctorConsent: payload.doctorConsent,
          prescriptionId: payload.prescriptionId,
          doctorName: payload.doctorName,
          doctorDocument: payload.doctorDocument,
          doctorLicenseState: payload.doctorLicenseState,
          doctorLicenseNumber: payload.doctorLicenseNumber,
          doctorLicenseType: payload.doctorLicenseType,
          doctorEmail: payload.doctorEmail,
        },
        {
          timeout: 10000,
        }
      );

      return data;
    } catch (error) {
      logger.error('[PBM - PreEnrollment] Erro ao criar pré-cadastro:', { error });
      throw error;
    }
  },
};
